import { db } from "./dbClient";

// ─── CaseManagement.jsx ───────────────────────────────────────────────────────
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { downloadCSV, printToPDF } from "./exportUtils";
import DateRangeFilter from "./DateRangeFilter";
import ConfirmDialog from "./ConfirmDialog";
import EmptyState from "./EmptyState";
import AuditLogPanel from "./AuditLogPanel";
import { useAuditLog } from "./useAuditLog";
import { DeadlineTableCell } from "./DeadlineDisplay";
import ManualCaseCreation from "./ManualCaseCreation";

function getAPIConfig() {
  try {
    return JSON.parse(localStorage.getItem("apiConfig")) || { tmSystemUrl: "", apiKey: "", lastSync: null, syncEnabled: false };
  } catch {
    return { tmSystemUrl: "", apiKey: "", lastSync: null, syncEnabled: false };
  }
}

async function syncAlertsFromAPI(apiConfig) {
  if (!apiConfig.tmSystemUrl || !apiConfig.apiKey) return null;
  try {
    const baseUrl = apiConfig.tmSystemUrl.replace(/\/$/, "");
    const response = await fetch(`${baseUrl}/api/alerts`, {
      method: "GET",
      headers: { "Authorization": `Bearer ${apiConfig.apiKey}`, "Content-Type": "application/json" },
    });
    if (!response.ok) return null;
    const data = await response.json();
    const config = getAPIConfig();
    config.lastSync = new Date().toISOString();
    localStorage.setItem("apiConfig", JSON.stringify(config));
    return Array.isArray(data) ? data : data.alerts || [];
  } catch { return null; }
}

// ─── Mergeable Groups Modal ───────────────────────────────────────────────────
function MergeableGroupsModal({ groups, onMergeGroup, onClose }) {
  const C = useTheme();
  const riskOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
  const riskColor = { CRITICAL: "#FF6B6B", HIGH: C.danger, MEDIUM: C.warn, LOW: C.success };

  // group key → Set of selected case IDs (all pre-selected)
  const [groupSelections, setGroupSelections] = useState(() => {
    const init = {};
    Object.entries(groups).forEach(([k, cases]) => {
      init[k] = new Set(cases.map(c => c.id));
    });
    return init;
  });
  const [pendingMerge, setPendingMerge] = useState(null); // array of cases to merge

  function toggleCase(groupKey, caseId) {
    setGroupSelections(prev => {
      const sel = new Set(prev[groupKey]);
      sel.has(caseId) ? sel.delete(caseId) : sel.add(caseId);
      return { ...prev, [groupKey]: sel };
    });
  }

  function toggleAll(groupKey, cases) {
    setGroupSelections(prev => {
      const allOn = cases.every(c => prev[groupKey]?.has(c.id));
      return { ...prev, [groupKey]: allOn ? new Set() : new Set(cases.map(c => c.id)) };
    });
  }

  const sortedGroups = Object.entries(groups)
    .sort(([, a], [, b]) => b.length - a.length);
  const totalEntities = sortedGroups.length;
  const totalCases    = sortedGroups.reduce((s, [, cs]) => s + cs.length, 0);

  return (
    <>
    {pendingMerge && (
      <ConfirmDialog
        open
        title={`Merge ${pendingMerge.length} cases?`}
        body={`This will combine ${pendingMerge.length} cases for "${pendingMerge[0]?.entity}" into one. The merged cases will be closed. This cannot be undone.`}
        confirmLabel="Merge Cases"
        onConfirm={() => { onMergeGroup(pendingMerge); setPendingMerge(null); }}
        onCancel={() => setPendingMerge(null)}
      />
    )}
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      paddingTop: 48, overflowY: "auto" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface,
        border: `1px solid ${C.border}`, borderRadius: 14, padding: 28,
        width: 680, maxWidth: "95vw", fontFamily: "'DM Mono',monospace",
        marginBottom: 48 }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
              Find Mergeable Cases
            </div>
            <div style={{ color: C.textDim, fontSize: 12 }}>
              {totalEntities} entity group{totalEntities !== 1 ? "s" : ""} · {totalCases} cases total.
              Check which cases to include, then click Merge.
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none",
            color: C.textDim, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "2px 6px" }}>✕</button>
        </div>

        {/* Entity groups */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {sortedGroups.map(([groupKey, cases]) => {
            const sel         = groupSelections[groupKey] || new Set();
            const selCount    = sel.size;
            const allChecked  = cases.every(c => sel.has(c.id));
            const someChecked = cases.some(c => sel.has(c.id));
            const selCases    = cases.filter(c => sel.has(c.id));
            const displayName = cases[0]?.entity || groupKey;
            const topRisk     = cases.reduce((b, c) => (riskOrder[c.risk] || 0) > (riskOrder[b.risk] || 0) ? c : b, cases[0]).risk;

            return (
              <div key={groupKey} style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
                {/* Group header bar */}
                <div style={{ padding: "12px 16px", background: C.bg,
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex", alignItems: "center", gap: 10 }}>
                  <input type="checkbox"
                    checked={allChecked}
                    ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={() => toggleAll(groupKey, cases)}
                    style={{ cursor: "pointer", accentColor: C.accent, width: 15, height: 15, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{displayName}</span>
                    <span style={{ marginLeft: 10, fontSize: 10, padding: "2px 8px", borderRadius: 4,
                      background: `${riskColor[topRisk] || C.textDim}18`,
                      color: riskColor[topRisk] || C.textDim, fontWeight: 700 }}>{topRisk}</span>
                    <span style={{ color: C.textDim, fontSize: 11, marginLeft: 8 }}>
                      {cases.length} cases
                    </span>
                  </div>
                  <button
                    disabled={selCount < 2}
                    onClick={() => selCount >= 2 && setPendingMerge(selCases)}
                    style={{ padding: "6px 16px", borderRadius: 7, border: "none",
                      fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: 12,
                      cursor: selCount < 2 ? "not-allowed" : "pointer",
                      background: selCount >= 2 ? C.accent : C.surfaceAlt,
                      color: selCount >= 2 ? C.bg : C.textDim,
                      opacity: selCount < 2 ? 0.5 : 1, whiteSpace: "nowrap" }}>
                    {selCount >= 2 ? `Merge ${selCount} Cases` : "Select 2+"}
                  </button>
                </div>

                {/* Individual cases */}
                {cases.map((c, idx) => {
                  const isChecked = sel.has(c.id);
                  const rc = riskColor[c.risk] || C.textDim;
                  const statusColors = { open: C.warn, in_review: C.accent, escalated: C.danger,
                    new: "#F0B429", pending_review: "#4A7FA5" };
                  return (
                    <div key={c.id} onClick={() => toggleCase(groupKey, c.id)}
                      style={{ display: "flex", alignItems: "center", gap: 12,
                        padding: "10px 16px", cursor: "pointer",
                        borderBottom: idx < cases.length - 1 ? `1px solid ${C.border}` : "none",
                        background: isChecked ? `${C.accent}07` : "transparent",
                        transition: "background 0.1s" }}>
                      <input type="checkbox" checked={isChecked}
                        onChange={() => toggleCase(groupKey, c.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ cursor: "pointer", accentColor: C.accent, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", gap: 7, alignItems: "center", marginBottom: 3, flexWrap: "wrap" }}>
                          <span style={{ color: C.accent, fontSize: 11 }}>{c.id}</span>
                          <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3,
                            background: `${rc}18`, color: rc, fontWeight: 700 }}>{c.risk}</span>
                          <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3,
                            background: `${statusColors[c.status] || C.textDim}15`,
                            color: statusColors[c.status] || C.textDim }}>
                            {c.status?.replace("_", " ")}
                          </span>
                          {c.country && (
                            <span style={{ fontSize: 10, color: C.textDim }}>{c.country}</span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 12, color: C.textMid, fontSize: 12 }}>
                          <span>{c.type}</span>
                          {c.amount && <span style={{ color: C.text, fontWeight: 600 }}>{c.amount}</span>}
                          {c.date && <span style={{ color: C.textDim }}>{c.date}</span>}
                        </div>
                        {c.notes && (
                          <div style={{ color: C.textDim, fontSize: 11, marginTop: 3,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 480 }}>
                            {c.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 20, color: C.textDim, fontSize: 11, textAlign: "center" }}>
          Cases shown are all non-closed, non-merged cases. Merged secondary cases will be marked as "merged".
        </div>
      </div>
    </div>
    </>
  );
}

function MergeModal({ cases, onConfirm, onCancel, merging }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [primaryId, setPrimaryId] = useState(cases[0]?.id || "");
  const primary   = cases.find(c => c.id === primaryId);
  const secondary = cases.filter(c => c.id !== primaryId);
  const riskOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
  const highestRisk = cases.reduce((best, c) => (riskOrder[c.risk]||0) > (riskOrder[best.risk]||0) ? c : best, cases[0]).risk;
  const allTypes    = [...new Set(cases.map(c => c.type).filter(Boolean))].join(" · ");
  const totalTx     = cases.reduce((sum, c) => sum + (parseInt(c.tx_count)||1), 0);

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:300,
      display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{ background:C.surface,
        border:`1px solid ${C.border}`, borderRadius:14, padding:32, width:580,
        maxHeight:"80vh", overflowY:"auto", fontFamily:"'DM Mono',monospace" }}>
        <div style={{ marginBottom:20 }}>
          <div style={{ color:C.text, fontFamily:"'Syne',sans-serif", fontSize:18,
            fontWeight:700, marginBottom:4 }}>{t('cases.merge.title', { count: cases.length })}</div>
          <div style={{ color:C.textDim, fontSize:12 }}>
            {t('cases.merge.subtitle')}
          </div>
        </div>
        <div style={{ marginBottom:20 }}>
          <div style={{ color:C.textMid, fontSize:11, letterSpacing:"0.08em", marginBottom:8 }}>
            {t('cases.merge.selectPrimary')}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {cases.map(c => (
              <button key={c.id} onClick={() => setPrimaryId(c.id)}
                style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px",
                  borderRadius:8, cursor:"pointer", textAlign:"left",
                  background:primaryId===c.id ? C.accentGlow : C.bg,
                  border:`1px solid ${primaryId===c.id ? C.accent : C.border}` }}>
                <div style={{ width:18, height:18, borderRadius:"50%",
                  border:`2px solid ${primaryId===c.id ? C.accent : C.border}`,
                  background:primaryId===c.id ? C.accent : "transparent", flexShrink:0,
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {primaryId===c.id && <div style={{ width:6, height:6, borderRadius:"50%", background:C.bg }} />}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                    <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace", fontSize:11 }}>{c.id}</span>
                    <span style={{ color:{HIGH:C.danger,MEDIUM:C.warn,LOW:C.success}[c.risk]||C.textDim, fontSize:10, fontWeight:700 }}>{c.risk}</span>
                    {primaryId===c.id && <span style={{ color:C.accent, fontSize:10, fontWeight:700 }}>{t('cases.merge.primaryLabel')}</span>}
                  </div>
                  <div style={{ color:C.text, fontSize:13, fontWeight:500 }}>{c.entity}</div>
                  <div style={{ color:C.textDim, fontSize:11 }}>{c.type} · {c.date}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div style={{ background:C.bg, border:`1px solid ${C.borderLight}`, borderRadius:8,
          padding:16, marginBottom:20 }}>
          <div style={{ color:C.textMid, fontSize:11, letterSpacing:"0.08em", marginBottom:10 }}>
            {t('cases.merge.preview')}
          </div>
          {[[t('cases.merge.caseId'),primary?.id],[t('cases.merge.entity'),primary?.entity],[t('cases.merge.alertTypes'),allTypes],
            [t('cases.merge.risk'),t('cases.merge.highestRisk', { risk: highestRisk })],[t('cases.merge.transactions'),t('cases.merge.combinedTx', { count: totalTx })],
            [t('cases.merge.secondaries'),t('cases.merge.willBeClosed', { count: secondary.length })]
          ].map(([k,v]) => (
            <div key={k} style={{ display:"flex", justifyContent:"space-between",
              fontSize:12, padding:"5px 0", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ color:C.textDim }}>{k}</span>
              <span style={{ color:C.text, fontFamily:"'DM Mono',monospace", fontSize:11,
                maxWidth:"60%", textAlign:"right" }}>{v}</span>
            </div>
          ))}
        </div>
        {secondary.length > 0 && (
          <div style={{ background:`${C.warn}08`, border:`1px solid ${C.warn}30`,
            borderRadius:7, padding:"10px 14px", marginBottom:20, fontSize:12, color:C.textMid }}>
            ⚠ {t('cases.merge.warningPrefix')} <span style={{ color:C.text, fontFamily:"'DM Mono',monospace" }}>
              {secondary.map(c=>c.id).join(", ")}
            </span> {t('cases.merge.warningClosed')}.
          </div>
        )}
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onCancel} style={{ flex:1, padding:"10px", background:"transparent",
            border:`1px solid ${C.border}`, borderRadius:8, color:C.textMid,
            cursor:"pointer", fontFamily:"'DM Mono',monospace", fontSize:13 }}>{t('cases.merge.cancel')}</button>
          <button onClick={() => onConfirm(primaryId)} disabled={merging}
            style={{ flex:2, padding:"10px", border:"none", borderRadius:8, fontWeight:700,
              background:merging ? C.border : C.accent, color:merging ? C.textDim : C.bg,
              cursor:merging ? "wait" : "pointer", fontFamily:"'DM Mono',monospace", fontSize:13 }}>
            {merging ? t('cases.merge.merging') : t('cases.merge.mergeInto', { id: primaryId })}
          </button>
        </div>
      </div>
    </div>
  );
}

function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const C = useTheme();
  const active = sortField === field;
  return (
    <th onClick={() => onSort(field)}
      style={{ textAlign:"left", padding:"8px 12px", color:active?C.accent:C.textDim,
        fontSize:10, letterSpacing:"0.1em", fontWeight:600, fontFamily:"'DM Mono',monospace",
        cursor:"pointer", userSelect:"none", whiteSpace:"nowrap" }}>
      {label}<span style={{ marginLeft:4, opacity:active?1:0.3 }}>
        {active ? (sortDir==="asc" ? "↑" : "↓") : "↕"}
      </span>
    </th>
  );
}

export default function CaseManagement({ alerts, setAlerts, team, onOpenCase, company, currentUser, agingFilter, setAgingFilter, customSLAThresholds = {} }) {
  const C = useTheme();
  const { t } = useTranslation();

  // Use custom SLA thresholds or defaults
  const SLA_DAYS = {
    CRITICAL: customSLAThresholds?.CRITICAL ?? 3,
    HIGH: customSLAThresholds?.HIGH ?? 7,
    MEDIUM: customSLAThresholds?.MEDIUM ?? 14,
    LOW: customSLAThresholds?.LOW ?? 21,
  };

  function getSLADaysRemaining(caseRow) {
    const slaLimit = SLA_DAYS[(caseRow.risk || "").toUpperCase()];
    if (!slaLimit) return null;
    // Calculate from alert generated date, not transaction date
    const alertDate = new Date(caseRow.alert_generated_at || caseRow.created_at || 0);
    const now = new Date();
    const daysSince = Math.floor((now - alertDate) / (1000 * 60 * 60 * 24));
    return slaLimit - daysSince;
  }

  function SLABadge({ caseRow }) {
    const status = caseRow.status;
    if (status === "closed" || status === "resolved" || status === "merged") return null;
    const rem = getSLADaysRemaining(caseRow);
    if (rem === null) return null;
    let color, label;
    if (rem < 0)      { color = C.danger;  label = "OVERDUE"; }
    else if (rem <= 2) { color = C.danger;  label = `${rem}d`; }
    else if (rem <= 5) { color = C.warn;    label = `${rem}d`; }
    else if (rem <= 10){ color = "#F0B429"; label = `${rem}d`; }
    else               { color = C.success; label = `${rem}d`; }
    return (
      <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, fontFamily: "'DM Mono',monospace",
        fontWeight: 700, color, background: `${color}18`, border: `1px solid ${color}40`, whiteSpace: "nowrap" }}>
        {label}
      </span>
    );
  }
  const [statusFilter,    setStatusFilter]    = useState("all");
  const [syncing,         setSyncing]         = useState(false);
  const [selected,        setSelected]        = useState(new Set());
  const [showMerge,       setShowMerge]       = useState(false);
  const [merging,         setMerging]         = useState(false);
  const [showBulkAssign,  setShowBulkAssign]  = useState(false);
  const [bulkAssigning,   setBulkAssigning]   = useState(false);
  const [sortField,       setSortField]       = useState("date");
  const [sortDir,         setSortDir]         = useState("desc");
  const [search,          setSearch]          = useState("");
  const [filterRisk,      setFilterRisk]      = useState("all");
  const [filterType,      setFilterType]      = useState("all");
  const [filterCountry,   setFilterCountry]   = useState("all");
  const [dateRange,       setDateRange]       = useState({ from: "", to: "" });
  const [confirm,         setConfirm]         = useState(null);
  const [showAudit,       setShowAudit]       = useState(false);
  const [showMergeGroups, setShowMergeGroups] = useState(false);
  const [casePage,        setCasePage]        = useState(1);
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [agingBucketFilter, setAgingBucketFilter] = useState(null);
  // Bulk action state (independent from filter/sort state)
  const [bulkIds,         setBulkIds]         = useState(new Set());
  const [bulkAssignee,    setBulkAssignee]    = useState("");
  const [bulkWorking,     setBulkWorking]     = useState(false);
  // Account numbers mapped by entity name
  const [accountNumbers,  setAccountNumbers]  = useState({});
  // Manual case creation modal
  const [showManualCaseModal, setShowManualCaseModal] = useState(false);
  const CASE_PAGE_SIZE = 25;

  const { log } = useAuditLog(company, currentUser);
  const apiConfig   = getAPIConfig();
  const riskColor   = { HIGH:C.danger, MEDIUM:C.warn, LOW:C.success };
  const statusColor = { open:C.warn, escalated:C.danger, closed:C.textDim,
    merged:C.textDim, new:"#F0B429", pending_review:"#4A7FA5" };

  // SLA / escalation
  const openStatuses = new Set(["open","new","in_review","pending_review","escalated"]);
  const overdueCases = useMemo(() =>
    alerts.filter(a => openStatuses.has(a.status) && getSLADaysRemaining(a) !== null && getSLADaysRemaining(a) < 0),
    [alerts]
  );

  useEffect(() => {
    if (agingFilter) {
      setAgingBucketFilter(agingFilter);
      setAgingFilter(null);
    }
  }, [agingFilter, setAgingFilter]);

  // Fetch account numbers for all entities
  useEffect(() => {
    async function fetchAccountNumbers() {
      if (!company?.id || !alerts?.length) return;
      const uniqueEntities = [...new Set(alerts.map(a => a.entity).filter(Boolean))];
      const newAccountNumbers = {};

      for (const entity of uniqueEntities) {
        try {
          const { data } = await db.from("kyc_submissions")
            .select("account_number")
            .eq("company_id", company.id)
            .ilike("full_name", `%${entity.trim()}%`)
            .order("created_at", { ascending: false })
            .limit(1)
            .execute();
          if (data && data.length > 0 && data[0].account_number) {
            newAccountNumbers[entity] = data[0].account_number;
          }
        } catch (e) {
          console.warn(`[Cases] Failed to fetch account number for ${entity}:`, e);
        }
      }
      setAccountNumbers(newAccountNumbers);
    }
    fetchAccountNumbers();
  }, [alerts, company?.id]);

  // Bulk action handlers
  function toggleBulk(id)    { setBulkIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  function toggleBulkAll()   { setBulkIds(bulkIds.size === filtered.length ? new Set() : new Set(filtered.map(a => a.id))); }
  async function bulkClose() {
    if (!bulkIds.size) return;
    setBulkWorking(true);
    const ids = [...bulkIds];
    const entities = alerts.filter(a => ids.includes(a.id)).map(a => a.entity).join(", ");
    const token = localStorage.getItem('amlguard_token');
    if (token && company?.id) {
      try {
        await fetch('/api/query?resource=cases', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            ids: ids,
            status: 'closed',
          }),
        });
      } catch (error) {
        console.warn('[Cases] Failed to update case status in Neon:', error.message);
      }
    }
    await log({ action: "cases_bulk_closed", module: "cases", entityName: `${ids.length} case(s) closed`, meta: { count: ids.length, case_ids: ids, entities } });
    setAlerts(prev => prev.map(a => ids.includes(a.id) ? { ...a, status: "closed" } : a));
    setBulkIds(new Set());
    setBulkWorking(false);
  }
  async function bulkInReview() {
    if (!bulkIds.size) return;
    setBulkWorking(true);
    const ids = [...bulkIds];
    const entities = alerts.filter(a => ids.includes(a.id)).map(a => a.entity).join(", ");
    const token = localStorage.getItem('amlguard_token');
    if (token && company?.id) {
      try {
        await fetch('/api/query?resource=cases', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            ids: ids,
            status: 'in_review',
          }),
        });
      } catch (error) {
        console.warn('[Cases] Failed to update case status in Neon:', error.message);
      }
    }
    await log({ action: "cases_bulk_in_review", module: "cases", entityName: `${ids.length} case(s) moved to In Review`, meta: { count: ids.length, case_ids: ids, entities } });
    setAlerts(prev => prev.map(a => ids.includes(a.id) ? { ...a, status: "in_review" } : a));
    setBulkIds(new Set());
    setBulkWorking(false);
  }
  async function bulkDelete() {
    if (!bulkIds.size) return;
    if (!window.confirm(`Permanently delete ${bulkIds.size} case(s)? This cannot be undone.`)) return;
    setBulkWorking(true);
    const ids = [...bulkIds];
    const casesToDelete = alerts.filter(a => ids.includes(a.id));
    const token = localStorage.getItem('amlguard_token');
    if (token && company?.id) {
      try {
        await fetch('/api/query?resource=cases', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ ids: ids }),
        });
      } catch (error) {
        alert(`Delete failed: ${error.message}`);
        setBulkWorking(false);
        return;
      }
    }
    await log({
      action: "cases_bulk_deleted",
      module: "cases",
      entityName: `${ids.length} case(s) permanently deleted`,
      meta: { count: ids.length, case_ids: ids, entities: casesToDelete.map(c => c.entity).join(", ") },
    });
    setAlerts(prev => prev.filter(a => !ids.includes(a.id)));
    setBulkIds(new Set());
    setBulkWorking(false);
  }
  async function bulkAssign() {
    if (!bulkIds.size || !bulkAssignee) return;
    setBulkWorking(true);
    const ids = [...bulkIds];
    const token = localStorage.getItem('amlguard_token');
    if (token && company?.id) {
      try {
        await fetch('/api/query?resource=cases', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            ids: ids,
            assigned_to: bulkAssignee,
          }),
        });
      } catch (error) {
        console.warn('[Cases] Failed to assign cases in Neon:', error.message);
      }
    }
    await log({ action: "cases_bulk_assigned", module: "cases", entityName: `${ids.length} case(s) assigned`, meta: { count: ids.length, case_ids: ids, assigned_to: bulkAssignee } });
    setAlerts(prev => prev.map(a => ids.includes(a.id) ? { ...a, assigned_to: bulkAssignee } : a));
    setBulkIds(new Set());
    setBulkAssignee("");
    setBulkWorking(false);
  }

  // ── Inline assignment ─────────────────────────────────────────────────────
  async function handleAssignCase(caseId, memberId, caseEntity) {
    if (!company?.id) return;

    setAlerts(prev => prev.map(a => a.id === caseId ? { ...a, assigned_to: memberId || null } : a));
    const member = (team || []).find(m => m.id === memberId || m.user_id === memberId);
    const memberName = member ? `${member.firstName || member.first_name || ""} ${member.lastName || member.last_name || ""}`.trim() || member.email : memberId;
    await log({
      action: "case_assigned",
      module: "cases",
      entityId: caseId,
      entityName: caseEntity || caseId,
      newValue: { assigned_to: memberId, analyst: memberName },
    });

    if (memberId) {
      try {
        const { error } = await db?.from("assignments").insert({
          company_id: company.id,
          assigned_by: currentUser?.id || null,
          assigned_to: memberId,
          case_id: caseId,
          assignment_type: "case",
          notes: caseEntity || caseId,
        }).execute();
        if (error) {
          console.error('[Cases] Failed to assign case:', error);
          return;
        }
      } catch (e) { console.warn("[assign]", e.message); }
    } else {
      try {
        await db?.from("assignments")
          .delete()
          .eq("case_id", caseId)
          .eq("company_id", company.id)
          .execute();
      } catch (e) { console.warn("[unassign]", e.message); }
    }

    // Also update the cases table's assigned_to column for persistence on reload
    try {
      const token = localStorage.getItem('amlguard_token');
      if (token) {
        await fetch('/api/query?resource=cases', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            id: caseId,
            company_id: company.id,
            assigned_to: memberId || null,
          }),
        });
      }
    } catch (e) { console.warn("[update cases table]", e.message); }
    try {
      // Look up the team member's actual user_id (auth UUID) in case memberId is a row ID
      const member = (team || []).find(m => m.id === memberId || m.user_id === memberId);
      const notifyUserId = member?.user_id || member?.id || memberId;
      const { error: nErr } = await db?.from("notifications").insert({
        company_id:  company.id,
        user_id:     notifyUserId,
        type:        "alert_assigned",
        title:       `Case assigned: ${caseEntity || caseId}`,
        message:     `You have been assigned case ${caseId}. Open Case Management to review it.`,
        link_module: "cases",
        entity_id:   caseId,
        read:        false,
      }).execute();
      if (nErr) console.warn("[notify] case notification failed:", nErr.message);
    } catch (e) { console.warn("[notify]", e.message); }
  }

  const uniqueRisks     = useMemo(() => ["all",...new Set(alerts.map(a=>a.risk).filter(Boolean))],[alerts]);
  const uniqueTypes     = useMemo(() => ["all",...new Set(alerts.map(a=>a.type).filter(Boolean))],[alerts]);
  const uniqueCountries = useMemo(() => ["all",...new Set(alerts.map(a=>a.country).filter(Boolean))],[alerts]);

  const entityGroups = useMemo(() => {
    const g = {};
    alerts.filter(a=>a.status==="open"||a.status==="new").forEach(a => {
      const k=(a.entity||"").toLowerCase().trim();
      if(!g[k]) g[k]=[];
      g[k].push(a.id);
    });
    return g;
  }, [alerts]);

  function hasDupe(entity) { return ((entityGroups[(entity||"").toLowerCase().trim()]||[]).length>1); }

  // Groups of 2+ non-closed/non-merged cases by entity — used by MergeableGroupsModal
  const mergeableGroups = useMemo(() => {
    const g = {};
    alerts
      .filter(a => a.status !== "closed" && a.status !== "merged")
      .forEach(a => {
        const k = (a.entity || "").toLowerCase().trim();
        if (!k) return;
        if (!g[k]) g[k] = [];
        g[k].push(a);
      });
    // Keep only groups with 2+ cases
    Object.keys(g).forEach(k => { if (g[k].length < 2) delete g[k]; });
    return g;
  }, [alerts]);

  const filtered = useMemo(() => {
    let rows = alerts;
    if (statusFilter !== "all") rows = rows.filter(a => {
      if (statusFilter === "closed") {
        return a.status === "closed" || a.status === "merged";
      }
      if (statusFilter === "pending_review") {
        return a.status === "pending_review" || a.status === "under_review";
      }
      return a.status === statusFilter;
    });
    if (showOverdueOnly) rows = rows.filter(a => openStatuses.has(a.status) && getSLADaysRemaining(a) !== null && getSLADaysRemaining(a) < 0);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(a => (a.entity||"").toLowerCase().includes(q) || (a.id||"").toLowerCase().includes(q));
    }
    if (filterRisk    !== "all") rows = rows.filter(a => a.risk    === filterRisk);
    if (filterType    !== "all") rows = rows.filter(a => a.type    === filterType);
    if (filterCountry !== "all") rows = rows.filter(a => a.country === filterCountry);
    if (dateRange.from || dateRange.to) {
      rows = rows.filter(a => {
        const d = new Date(a.date || a.created_at || 0);
        if (dateRange.from && d < new Date(dateRange.from)) return false;
        if (dateRange.to   && d > new Date(dateRange.to + "T23:59:59")) return false;
        return true;
      });
    }
    if (agingBucketFilter) {
      const SLA_DAYS = { CRITICAL: 3, HIGH: 7, MEDIUM: 14, LOW: 21 };
      const now = new Date();
      rows = rows.filter(a => {
        if (a.status !== "open") return false;
        // Calculate SLA days remaining
        const slaLimit = SLA_DAYS[(a.risk || "").toUpperCase()];
        if (!slaLimit) return false;
        const alertDate = new Date(a.alert_generated_at || a.created_at || 0);
        const daysSince = Math.floor((now - alertDate) / (1000 * 60 * 60 * 24));
        const daysRemaining = slaLimit - daysSince;

        // Normalize filter label (replace en-dashes with regular hyphens for comparison)
        const normalizedFilter = agingBucketFilter.replace(/–/g, "-");

        // Filter by overdue buckets
        if (normalizedFilter === "On Track") return daysRemaining >= 0;
        if (normalizedFilter === "1-7 days overdue") return daysRemaining < 0 && daysRemaining >= -7;
        if (normalizedFilter === "7-14 days overdue") return daysRemaining < -7 && daysRemaining >= -14;
        if (normalizedFilter === "14-21 days overdue") return daysRemaining < -14 && daysRemaining >= -21;
        if (normalizedFilter === "+21 days overdue") return daysRemaining < -21;
        // If filter is set but doesn't match, show nothing (not all cases)
        return false;
      });
    }
    return [...rows].sort((a,b) => {
      let va=a[sortField]??"", vb=b[sortField]??"";

      // Special handling for aging (SLA days remaining)
      if (sortField === "aging") {
        const SLA_DAYS = { CRITICAL: 3, HIGH: 7, MEDIUM: 14, LOW: 21 };
        const now = new Date();

        const calcRemaining = (c) => {
          const slaLimit = SLA_DAYS[(c.risk || "").toUpperCase()];
          if (!slaLimit) return 999999; // Cases with no risk sort to bottom
          const alertDate = new Date(c.alert_generated_at || c.created_at || 0);
          const daysSince = Math.floor((now - alertDate) / (1000 * 60 * 60 * 24));
          return slaLimit - daysSince;
        };

        va = calcRemaining(a);
        vb = calcRemaining(b);
      } else if (sortField==="amount") {
        va=parseFloat(String(va).replace(/[^0-9.-]/g,""))||0;
        vb=parseFloat(String(vb).replace(/[^0-9.-]/g,""))||0;
      } else if (sortField==="date") {
        va=new Date(va).getTime()||0;
        vb=new Date(vb).getTime()||0;
      } else {
        va=String(va).toLowerCase();
        vb=String(vb).toLowerCase();
      }

      if (va<vb) return sortDir==="asc"?-1:1;
      if (va>vb) return sortDir==="asc"?1:-1;
      return 0;
    });
  }, [alerts,statusFilter,showOverdueOnly,search,filterRisk,filterType,filterCountry,dateRange,sortField,sortDir,agingBucketFilter]);

  const totalCasePages = Math.max(1, Math.ceil(filtered.length / CASE_PAGE_SIZE));
  const paginated = filtered.slice((casePage - 1) * CASE_PAGE_SIZE, casePage * CASE_PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => setCasePage(1), [statusFilter, showOverdueOnly, search, filterRisk, filterType, filterCountry, dateRange]);

  function handleSort(field) {
    if (sortField===field) setSortDir(d=>d==="asc"?"desc":"asc");
    else { setSortField(field); setSortDir("asc"); }
  }
  function toggleSelect(id)  { setSelected(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; }); }
  function toggleSelectAll() { setSelected(selected.size===filtered.length ? new Set() : new Set(filtered.map(a=>a.id))); }

  const selectedCases = alerts.filter(a => selected.has(a.id));
  const hasDuplicates = Object.values(entityGroups).some(g => g.length > 1);

  async function handleSync() { setSyncing(true); const s=await syncAlertsFromAPI(apiConfig); if(s&&Array.isArray(s)) setAlerts(s); setSyncing(false); }

  async function handleBulkAssign(assigneeId) {
    if (!selected.size || !assigneeId) {
      console.error('[BulkAssign] Missing required params: selected.size=', selected.size, 'assigneeId=', assigneeId);
      return;
    }
    setBulkAssigning(true);
    const ids = Array.from(selected);
    const token = localStorage.getItem('amlguard_token');
    console.log('[BulkAssign] Starting with', ids.length, 'cases, token exists:', !!token, 'company:', company?.id);

    try {
      if (!token || !company?.id) {
        console.error('[BulkAssign] Missing token or company:', { hasToken: !!token, hasCompany: !!company?.id });
        setBulkAssigning(false);
        return;
      }

      if (token && company?.id) {
        // Update each case individually
        const results = await Promise.all(ids.map(async caseId => {
          const response = await fetch('/api/query?resource=cases', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              id: caseId,
              assigned_to: assigneeId,
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`[BulkAssign] Failed to assign case ${caseId}: ${response.status} - ${errorText}`);
            return { success: false, caseId, status: response.status, error: errorText };
          }
          return { success: true, caseId };
        }));

        const failedCount = results.filter(r => !r.success).length;
        if (failedCount > 0) {
          console.warn(`[BulkAssign] ${failedCount} out of ${ids.length} cases failed to assign`);
        }
      }

      const member = team?.find(m => m.id === assigneeId);
      const memberName = member ? `${member.firstName || member.first_name || ""} ${member.lastName || member.last_name || ""}`.trim() || member.email : assigneeId;

      await log({
        action: "cases_bulk_assigned",
        module: "cases",
        entityName: `${ids.length} case(s) assigned to ${memberName}`,
        meta: { count: ids.length, case_ids: ids, assigned_to: assigneeId }
      });

      setAlerts(prev => prev.map(a => ids.includes(a.id) ? { ...a, assigned_to: assigneeId } : a));
      setSelected(new Set());
      setShowBulkAssign(false);
      setBulkAssignee(null);
    } catch (error) {
      console.error('[BulkAssign] Error:', error);
    } finally {
      setBulkAssigning(false);
    }
  }

  function handleMergeGroup(groupCases) {
    setSelected(new Set(groupCases.map(c => c.id)));
    setShowMergeGroups(false);
    setShowMerge(true);
  }

  async function handleMergeConfirm(primaryId) {
    setMerging(true);
    try {
      const primary   = alerts.find(a => a.id === primaryId);
      const secondary = selectedCases.filter(a => a.id !== primaryId);
      if (!primary) return;
      const riskOrder   = { HIGH:3, MEDIUM:2, LOW:1 };
      const highestRisk = selectedCases.reduce((best,c)=>(riskOrder[c.risk]||0)>(riskOrder[best.risk]||0)?c:best, primary).risk;
      const allTypes    = [...new Set(selectedCases.map(c=>c.type).filter(Boolean))].join(" · ");
      const totalTx     = selectedCases.reduce((sum,c)=>sum+(parseInt(c.tx_count)||1), 0);
      const mergedNotes = [primary.notes, `[Merged ${secondary.length} case(s): ${secondary.map(c=>c.id).join(", ")}]`, ...secondary.map(c=>c.notes).filter(Boolean)].filter(Boolean).join("\n");

      const token = localStorage.getItem('amlguard_token');
      if (token && company?.id) {
        try {
          // Update primary case
          await fetch('/api/query?resource=cases', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              id: primaryId,
              risk: highestRisk,
              type: allTypes,
              tx_count: totalTx,
              notes: mergedNotes,
            }),
          });

          // Mark secondary cases as merged
          for (const sec of secondary) {
            await fetch('/api/query?resource=cases', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                id: sec.id,
                status: 'merged',
                notes: `[Merged into ${primaryId}]${sec.notes?" · "+sec.notes:""}`,
                closed_at: new Date().toISOString(),
              }),
            });
          }
        } catch (error) {
          console.warn('[Cases] Failed to merge cases in Neon:', error.message);
          return;
        }
      }

      await log({ action:"cases_merged", module:"cases", entityId:primaryId, entityName:primary.entity,
        meta:{ merged_ids: secondary.map(c=>c.id) } });

      const { data } = await db?.from("cases").eq("company_id", company?.id).order("created_at", { ascending: false }).execute();
      setAlerts(data||[]);
      setSelected(new Set());
      setShowMerge(false);
    } catch (err) {
      console.error("[merge]", err);
    } finally {
      setMerging(false);
    }
  }

  // ── Clear all with confirm ───────────────────────────────────────────────
  function triggerClearAll() {
    setConfirm({
      title: t('cases.clearAllTitle'),
      body:  "This will permanently delete all cases from the database.",
      confirmLabel: t('common.clearAll'),
      onConfirm: async () => {
        setConfirm(null);
        const count = alerts.length;
        const token = localStorage.getItem('amlguard_token');
        if (token && company?.id && alerts.length > 0) {
          try {
            const ids = alerts.map(a => a.id);
            await fetch('/api/query?resource=cases', {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ ids }),
            });
          } catch (error) {
            alert(`Delete failed: ${error.message}`);
            return;
          }
        } else if (!token) {
          alert('Not authenticated');
          return;
        }
        await log({
          action: "cases_cleared_all",
          module: "cases",
          entityName: "All cases permanently deleted",
          meta: { count, confirmed_delete: true },
        });
        setAlerts([]);
      },
    });
  }

  // ── CSV export ───────────────────────────────────────────────────────────
  function handleExportCSV() {
    downloadCSV(filtered.map(a => ({
      id: a.id, entity: a.entity, amount: a.amount, type: a.type,
      risk: a.risk, country: a.country, status: a.status,
      date: a.date || a.created_at?.slice(0,10),
    })), `crossregguard_cases_${Date.now()}.csv`);
  }

  // ── PDF export ───────────────────────────────────────────────────────────
  function handleExportPDF() {
    const rows = filtered.slice(0, 100).map(a =>
      `<tr><td>${a.id}</td><td>${a.entity||"—"}</td><td>${a.amount||"—"}</td><td>${a.type||"—"}</td>
       <td>${a.risk||"—"}</td><td>${a.country||"—"}</td><td>${a.status||"—"}</td>
       <td>${a.date||a.created_at?.slice(0,10)||"—"}</td></tr>`
    ).join("");
    printToPDF("Case Management Report", `
      <div class="section">
        <table><thead><tr><th>ID</th><th>Entity</th><th>Amount</th><th>Type</th>
          <th>Risk</th><th>Country</th><th>Status</th><th>Date</th></tr></thead>
        <tbody>${rows}</tbody></table>
      </div>`, company?.name);
  }

  const filterSelect = (value, onChange, options, placeholder) => (
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{ background:value!=="all"?C.accentGlow:C.bg,
        border:`1px solid ${value!=="all"?C.accent:C.border}`,
        borderRadius:6, color:value!=="all"?C.accent:C.textMid,
        fontSize:11, padding:"6px 8px", fontFamily:"'DM Mono',monospace",
        cursor:"pointer", outline:"none" }}>
      <option value="all">{placeholder}</option>
      {options.filter(o=>o!=="all").map(o=><option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div>
          <h1 style={{ color:C.text, fontFamily:"'Syne',sans-serif", fontSize:26, fontWeight:700, margin:0 }}>
            {t('cases.title')}
          </h1>
          <p style={{ color:C.textMid, margin:"4px 0 0", fontSize:14 }}>
            {selected.size>0
              ? (selected.size===1 ? t('cases.selectedCases', { count: selected.size }) : t('cases.selectedCasesPlural', { count: selected.size }))
              : t('cases.ofCases', { filtered: filtered.length, total: alerts.length })}
          </p>
        </div>
        <div style={{ display:"flex", gap:7, alignItems:"center", flexWrap:"wrap" }}>
          <button onClick={()=>setShowManualCaseModal(true)}
            style={{ padding:"7px 16px", borderRadius:7, cursor:"pointer", fontSize:12,
              fontWeight:700, background:C.accent, border:"none", color:C.bg,
              fontFamily:"'DM Mono',monospace" }}>
            + Create Case
          </button>
          {selected.size>=2 && (
            <button onClick={()=>setShowMerge(true)}
              style={{ padding:"7px 16px", borderRadius:7, cursor:"pointer", fontSize:12,
                fontWeight:700, background:C.accent, border:"none", color:C.bg,
                fontFamily:"'DM Mono',monospace" }}>
              {t('cases.mergeButton', { count: selected.size })}
            </button>
          )}
          {selected.size>0 && (
            <button onClick={()=>setShowBulkAssign(true)}
              style={{ padding:"7px 16px", borderRadius:7, cursor:"pointer", fontSize:12,
                fontWeight:700, background:C.accent, border:"none", color:C.bg,
                fontFamily:"'DM Mono',monospace" }}>
              👤 Assign ({selected.size})
            </button>
          )}
          {selected.size>0 && (
            <button onClick={()=>setSelected(new Set())}
              style={{ padding:"6px 13px", borderRadius:7, cursor:"pointer", fontSize:12,
                background:"transparent", border:`1px solid ${C.border}`,
                color:C.textMid, fontFamily:"'DM Mono',monospace" }}>
              {t('cases.clearSelection')}
            </button>
          )}
          {["all","open","pending_review","escalated","closed"].map(f=>(
            <button key={f} onClick={()=>setStatusFilter(f)}
              style={{ padding:"6px 13px", borderRadius:7, cursor:"pointer", fontSize:12,
                background:statusFilter===f?C.accentGlow:"transparent",
                border:`1px solid ${statusFilter===f?C.accent:C.border}`,
                color:statusFilter===f?C.accent:C.textMid, fontFamily:"'DM Mono',monospace",
                textTransform:"capitalize" }}>
              {f==="pending_review" ? t('cases.statusLabels.pending_review') : t(`cases.statusLabels.${f}`, { defaultValue: f })}
            </button>
          ))}
          {agingBucketFilter && (
            <button onClick={()=>setAgingBucketFilter(null)}
              style={{ padding:"6px 13px", borderRadius:7, cursor:"pointer", fontSize:12,
                background:`${C.accent}15`,
                border:`1px solid ${C.accent}`,
                color:C.accent, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
              ✓ {agingBucketFilter} <span style={{marginLeft:6}}>×</span>
            </button>
          )}
          <button onClick={handleExportCSV}
            style={{ padding:"6px 13px", borderRadius:7, cursor:"pointer", fontSize:12,
              background:"transparent", border:`1px solid ${C.border}`,
              color:C.textMid, fontFamily:"'DM Mono',monospace" }}>
            {t('common.exportCSV')}
          </button>
          <button onClick={handleExportPDF}
            style={{ padding:"6px 13px", borderRadius:7, cursor:"pointer", fontSize:12,
              background:"transparent", border:`1px solid ${C.border}`,
              color:C.textMid, fontFamily:"'DM Mono',monospace" }}>
            {t('common.exportPDF')}
          </button>
          <button onClick={triggerClearAll}
            style={{ padding:"6px 13px", borderRadius:7, cursor:"pointer", fontSize:12,
              background:"transparent", border:`1px solid ${C.danger}40`,
              color:C.danger, fontFamily:"'DM Mono',monospace" }}>
            {t('cases.clearAll')}
          </button>
          <button onClick={()=>setShowAudit(a=>!a)}
            style={{ padding:"6px 13px", borderRadius:7, cursor:"pointer", fontSize:12,
              background:showAudit?C.accentGlow:"transparent",
              border:`1px solid ${showAudit?C.accent:C.border}`,
              color:showAudit?C.accent:C.textMid, fontFamily:"'DM Mono',monospace" }}>
            {t('cases.activity')}
          </button>
        </div>
      </div>

      {/* SLA Escalation Banner */}
      {overdueCases.length > 0 && (
        <div
          onClick={() => setShowOverdueOnly(v => !v)}
          style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px",
            background:"rgba(255,68,68,0.1)", border:`1px solid ${C.danger}50`,
            borderRadius:8, marginBottom:14, cursor:"pointer",
            transition:"background 0.15s",
            ...(showOverdueOnly ? { background:"rgba(255,68,68,0.18)" } : {}) }}>
          <span style={{ fontSize:15 }}>⚠</span>
          <span style={{ color:C.danger, fontWeight:700, fontSize:13 }}>
            {overdueCases.length} case{overdueCases.length !== 1 ? "s" : ""} overdue — immediate action required
          </span>
          <span style={{ color:C.danger, fontSize:11, marginLeft:"auto", opacity:0.7 }}>
            {showOverdueOnly ? "Show all" : "Filter to overdue"}
          </span>
        </div>
      )}

      {/* Bulk action toolbar */}
      {bulkIds.size > 0 && (
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px",
          background:C.surface, border:`1px solid ${C.accent}40`, borderRadius:8,
          marginBottom:14, flexWrap:"wrap" }}>
          <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace", fontSize:12, fontWeight:700 }}>
            {bulkIds.size} selected
          </span>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <select value={bulkAssignee} onChange={e => setBulkAssignee(e.target.value)}
              style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:5,
                color:C.textMid, fontSize:11, padding:"5px 8px",
                fontFamily:"'DM Mono',monospace", cursor:"pointer", outline:"none" }}>
              <option value="">Assign to…</option>
              {(team||[]).map(m => (
                <option key={m.id} value={m.id}>{m.firstName||m.first_name} {m.lastName||m.last_name}</option>
              ))}
            </select>
            {bulkAssignee && (
              <button onClick={bulkAssign} disabled={bulkWorking}
                style={{ padding:"5px 12px", borderRadius:6, fontSize:11,
                  background:C.accent, border:"none", color:C.bg,
                  fontFamily:"'DM Mono',monospace", cursor:bulkWorking?"wait":"pointer",
                  opacity:bulkWorking?0.6:1 }}>
                Assign
              </button>
            )}
          </div>
          <button onClick={bulkClose} disabled={bulkWorking}
            style={{ padding:"5px 12px", borderRadius:6, fontSize:11,
              background:"transparent", border:`1px solid ${C.border}`,
              color:C.textMid, fontFamily:"'DM Mono',monospace",
              cursor:bulkWorking?"wait":"pointer", opacity:bulkWorking?0.6:1 }}>
            Close Selected
          </button>
          <button onClick={bulkInReview} disabled={bulkWorking}
            style={{ padding:"5px 12px", borderRadius:6, fontSize:11,
              background:"transparent", border:`1px solid ${C.border}`,
              color:C.textMid, fontFamily:"'DM Mono',monospace",
              cursor:bulkWorking?"wait":"pointer", opacity:bulkWorking?0.6:1 }}>
            Mark In Review
          </button>
          <button onClick={bulkDelete} disabled={bulkWorking}
            style={{ padding:"5px 12px", borderRadius:6, fontSize:11,
              background:"transparent", border:"1px solid #FF4444",
              color:"#FF4444", fontFamily:"'DM Mono',monospace",
              cursor:bulkWorking?"wait":"pointer", opacity:bulkWorking?0.6:1 }}>
            Delete Selected
          </button>
          <button onClick={() => setBulkIds(new Set())}
            style={{ padding:"5px 12px", borderRadius:6, fontSize:11,
              background:"transparent", border:`1px solid ${C.border}`,
              color:C.textDim, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
            Clear Selection
          </button>
        </div>
      )}

      {/* Date filter */}
      <div style={{ marginBottom:12 }}>
        <DateRangeFilter from={dateRange.from} to={dateRange.to} onChange={setDateRange} />
      </div>

      {/* Search + filter row */}
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder={t('cases.searchPlaceholder')}
          style={{ flex:1, minWidth:200, padding:"7px 12px", background:C.bg,
            border:`1px solid ${search?C.accent:C.border}`, borderRadius:7,
            color:C.text, fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none" }} />
        {filterSelect(filterRisk,    setFilterRisk,    uniqueRisks,     t('cases.allRisks'))}
        {filterSelect(filterType,    setFilterType,    uniqueTypes,     t('cases.allTypes'))}
        {filterSelect(filterCountry, setFilterCountry, uniqueCountries, t('cases.allCountries'))}
        <button onClick={() => setShowOverdueOnly(v => !v)}
          style={{ padding:"6px 12px", borderRadius:6, fontSize:11,
            background:showOverdueOnly?"rgba(255,68,68,0.15)":"transparent",
            border:`1px solid ${showOverdueOnly?C.danger:C.border}`,
            color:showOverdueOnly?C.danger:C.textDim,
            cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
          Show Overdue Only
        </button>
        {(search||filterRisk!=="all"||filterType!=="all"||filterCountry!=="all"||dateRange.from||dateRange.to||showOverdueOnly) && (
          <button onClick={()=>{ setSearch(""); setFilterRisk("all"); setFilterType("all"); setFilterCountry("all"); setDateRange({from:"",to:""}); setShowOverdueOnly(false); }}
            style={{ padding:"6px 12px", borderRadius:6, background:"transparent",
              border:`1px solid ${C.border}`, color:C.textDim, fontSize:11,
              cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
            {t('cases.clearFilters')}
          </button>
        )}
      </div>

      {/* Duplicate hint */}
      {hasDuplicates && selected.size===0 && (
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px",
          background:`${C.accent}08`, border:`1px solid ${C.accent}30`,
          borderRadius:7, marginBottom:14, fontSize:12 }}>
          <span style={{ color:C.accent }}>◈</span>
          <span style={{ color:C.textMid, flex:1 }}>{t('cases.duplicateHint')}</span>
          <button onClick={() => setShowMergeGroups(true)}
            style={{ padding:"5px 14px", borderRadius:6, border:"none", cursor:"pointer",
              background:C.accent, color:C.bg, fontFamily:"'DM Mono',monospace",
              fontWeight:700, fontSize:11, whiteSpace:"nowrap" }}>
            Find Mergeable Cases
          </button>
        </div>
      )}

      {/* API sync bar */}
      <div style={{ display:"flex", gap:12, marginBottom:18 }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:10, padding:"9px 14px",
          background:apiConfig.tmSystemUrl?"rgba(0,230,118,0.05)":"rgba(255,140,0,0.05)",
          border:`1px solid ${apiConfig.tmSystemUrl?"rgba(0,230,118,0.18)":"rgba(255,140,0,0.18)"}`,
          borderRadius:7 }}>
          <div style={{ width:7, height:7, borderRadius:"50%",
            background:apiConfig.tmSystemUrl?C.success:C.warn,
            boxShadow:`0 0 6px ${apiConfig.tmSystemUrl?C.success:C.warn}` }} />
          <span style={{ color:C.textMid, fontSize:13 }}>
            {apiConfig.tmSystemUrl
              ? `${t('cases.tmApiConnected')}${apiConfig.lastSync ? t('cases.tmApiLastSync', { time: new Date(apiConfig.lastSync).toLocaleTimeString() }) : ""}`
              : t('cases.tmApiNotConfigured')}
          </span>
        </div>
        {apiConfig.tmSystemUrl && (
          <button onClick={handleSync} disabled={syncing}
            style={{ padding:"8px 16px", background:C.accent, border:"none", borderRadius:7,
              color:C.bg, fontWeight:600, cursor:syncing?"wait":"pointer",
              fontFamily:"'DM Mono',monospace", fontSize:12, opacity:syncing?0.6:1 }}>
            {syncing ? t('common.syncing') : t('common.syncNow')}
          </button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState
          icon="◈"
          title={t('cases.noCasesMatch')}
          body={alerts.length === 0
            ? t('cases.noCasesBody')
            : t('cases.adjustFilters')}
          action={alerts.length === 0 ? undefined : t('cases.clearFilters')}
          onAction={() => { setSearch(""); setFilterRisk("all"); setFilterType("all"); setFilterCountry("all"); setStatusFilter("all"); setDateRange({from:"",to:""}); }}
        />
      ) : (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                <th style={{ padding:"6px 8px", width:50, textAlign:"center" }}>
                  <div style={{ color:C.textDim, fontSize:9, letterSpacing:"0.1em", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:6 }}>BULK</div>
                  <input type="checkbox"
                    checked={filtered.length>0&&bulkIds.size===filtered.length}
                    onChange={toggleBulkAll}
                    style={{ cursor:"pointer", accentColor:C.accent }} />
                </th>
                <th style={{ padding:"6px 8px", width:50, textAlign:"center" }}>
                  <div style={{ color:C.textDim, fontSize:9, letterSpacing:"0.1em", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:6 }}>MERGE</div>
                  <input type="checkbox"
                    checked={filtered.length>0&&selected.size===filtered.length}
                    onChange={toggleSelectAll}
                    style={{ cursor:"pointer", accentColor:C.accent }} />
                </th>
                <SortHeader label={t('cases.headers.caseId')}    field="id"      sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('cases.headers.entity')}     field="entity"  sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th style={{ padding:"8px 12px", color:C.textDim, fontSize:10, letterSpacing:"0.1em", fontFamily:"'DM Mono',monospace" }}>ACCOUNT NUMBER</th>
                <SortHeader label={t('cases.headers.amount')}     field="amount"  sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('cases.headers.alertType')}  field="type"    sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('cases.headers.risk')}       field="risk"    sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('cases.headers.country')}    field="country" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('cases.headers.status')}     field="status"  sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('cases.headers.date')}       field="date"    sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="AGING"                         field="aging"   sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label={t('cases.headers.dueDate')}    field="dueDate" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th style={{ padding:"8px 12px", color:C.textDim, fontSize:10, letterSpacing:"0.1em", fontFamily:"'DM Mono',monospace" }}>AGING</th>
                <th style={{ padding:"8px 12px", color:C.textDim, fontSize:10, letterSpacing:"0.1em", fontFamily:"'DM Mono',monospace" }}>{t('cases.headers.assignedTo')}</th>
                <th style={{ padding:"8px 12px" }} />
              </tr>
            </thead>
            <tbody>
              {paginated.map(a => {
                const isSelected = selected.has(a.id);
                const isBulked   = bulkIds.has(a.id);
                const isDupe     = hasDupe(a.entity);
                // Calculate due date from alert generation + SLA, not transaction date
                const baseDate = a.alert_generated_at || a.created_at;
                const slaLimit = SLA_DAYS[(a.risk || "").toUpperCase()];
                const due = a.dueDate || (baseDate && slaLimit ? new Date(new Date(baseDate).getTime() + slaLimit * 24 * 60 * 60 * 1000).toISOString().split("T")[0] : null);
                const overdue    = due&&new Date(due)<new Date();
                return (
                  <tr key={a.id}
                    style={{ borderBottom:`1px solid ${C.border}`,
                      background:isBulked?`${C.accent}0A`:isSelected?`${C.accent}08`:"transparent",
                      transition:"background 0.1s" }}
                    onMouseEnter={e=>{ if(!isSelected&&!isBulked) e.currentTarget.style.background=C.surfaceAlt; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background=isBulked?`${C.accent}0A`:isSelected?`${C.accent}08`:"transparent"; }}>
                    <td style={{ padding:"10px 12px" }}>
                      <input type="checkbox" checked={isBulked} onChange={()=>toggleBulk(a.id)}
                        style={{ cursor:"pointer", accentColor:C.accent }} />
                    </td>
                    <td style={{ padding:"10px 12px" }}>
                      <input type="checkbox" checked={isSelected} onChange={()=>toggleSelect(a.id)}
                        style={{ cursor:"pointer", accentColor:C.accent }} />
                    </td>
                    <td style={{ padding:"10px 12px", color:C.accent, fontFamily:"'DM Mono',monospace", fontSize:11 }}>{a.id}</td>
                    <td style={{ padding:"10px 12px", color:C.text, fontSize:13, fontWeight:500 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        {a.entity}
                        {isDupe && <span title={t('cases.duplicateEntity')} style={{ color:C.accent, fontSize:11 }}>◈</span>}
                      </div>
                    </td>
                    <td style={{ padding:"10px 12px", color:C.text, fontFamily:"'DM Mono',monospace", fontSize:12 }}>
                      {accountNumbers[a.entity] ? (
                        <span style={{ color:C.accent, fontWeight:600 }}>{accountNumbers[a.entity]}</span>
                      ) : (
                        <span style={{ color:C.textDim }}>—</span>
                      )}
                    </td>
                    <td style={{ padding:"10px 12px", color:C.text, fontFamily:"'DM Mono',monospace", fontSize:12 }}>{a.amount}</td>
                    <td style={{ padding:"10px 12px", color:C.textMid, fontSize:12 }}>{a.type}</td>
                    <td style={{ padding:"10px 12px" }}>
                      <span style={{ color:riskColor[a.risk]||C.textDim, fontSize:11, fontWeight:700 }}>{a.risk}</span>
                    </td>
                    <td style={{ padding:"10px 12px", color:C.textMid, fontSize:12 }}>{a.country}</td>
                    <td style={{ padding:"10px 12px" }}>
                      <span style={{ color:statusColor[a.status]||C.textDim, fontSize:11, textTransform:"uppercase" }}>
                        {a.status==="merged" ? t('cases.statusLabels.merged') : a.status}
                      </span>
                    </td>
                    <td style={{ padding:"10px 12px", color:C.textDim, fontSize:12 }}>{a.date}</td>
                    <td style={{ padding:"8px 12px" }}>
                      {a.status === "open" || a.status === "new" || a.status === "in_review" || a.status === "pending_review" || a.status === "escalated" ? (
                        <DeadlineTableCell
                          alertGeneratedAt={a.alert_generated_at || a.created_at}
                          regulatoryDueDate={a.regulatory_due_date || due}
                          internalSLADueDate={a.internal_sla_due_date}
                        />
                      ) : (
                        <span style={{ color: C.textDim, fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding:"10px 12px", fontSize:12 }}>
                      {due
                        ? <span style={{ color:overdue?C.danger:C.textMid, fontWeight:overdue?700:400 }}>{due}{overdue?" ⚠":""}</span>
                        : <span style={{ color:C.textDim }}>—</span>}
                    </td>
                    <td style={{ padding:"10px 12px" }}>
                      <SLABadge caseRow={a} />
                    </td>
                    <td style={{ padding:"8px 12px" }}>
                      <select
                        value={a.assigned_to || ""}
                        onChange={e => handleAssignCase(a.id, e.target.value || null, a.entity)}
                        onClick={e => e.stopPropagation()}
                        style={{ background:a.assigned_to ? C.accentGlow : C.bg,
                          border:`1px solid ${a.assigned_to ? C.accent : C.border}`,
                          borderRadius:5, color:a.assigned_to ? C.accent : C.textDim,
                          fontSize:11, padding:"4px 8px", fontFamily:"'DM Mono',monospace",
                          cursor:"pointer", outline:"none", maxWidth:130 }}>
                        <option value="">{t('common.unassigned')}</option>
                        {(team||[]).map(m => (
                          <option key={m.id} value={m.id}>
                            {m.firstName||m.first_name} {m.lastName||m.last_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding:"10px 12px" }}>
                      <button onClick={()=>onOpenCase(a)}
                        style={{ padding:"5px 12px", background:C.accentGlow,
                          border:`1px solid ${C.borderLight}`, borderRadius:5,
                          color:C.accent, fontSize:11, cursor:"pointer",
                          fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap" }}>
                        {t('common.open')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalCasePages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "14px 0 4px", flexWrap: "wrap" }}>
              <button onClick={() => setCasePage(p => Math.max(1, p - 1))} disabled={casePage === 1}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: casePage === 1 ? C.textDim : C.text, cursor: casePage === 1 ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>←</button>
              {Array.from({ length: totalCasePages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setCasePage(p)}
                  style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${p === casePage ? C.accent : C.border}`, background: p === casePage ? C.accentGlow : "transparent", color: p === casePage ? C.accent : C.textMid, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: p === casePage ? 700 : 400 }}>{p}</button>
              ))}
              <button onClick={() => setCasePage(p => Math.min(totalCasePages, p + 1))} disabled={casePage === totalCasePages}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: casePage === totalCasePages ? C.textDim : C.text, cursor: casePage === totalCasePages ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>→</button>
              <span style={{ color: C.textDim, fontSize: 11, marginLeft: 6 }}>{filtered.length} total · page {casePage} of {totalCasePages}</span>
            </div>
          )}
        </div>
      )}

      {/* Audit log panel */}
      {showAudit && <AuditLogPanel company={company} module="cases" />}

      {/* Mergeable groups modal */}
      {showMergeGroups && Object.keys(mergeableGroups).length > 0 && (
        <MergeableGroupsModal
          groups={mergeableGroups}
          onMergeGroup={handleMergeGroup}
          onClose={() => setShowMergeGroups(false)}
        />
      )}

      {/* Merge modal */}
      {showMerge && (
        <MergeModal cases={selectedCases} onConfirm={handleMergeConfirm}
          onCancel={()=>setShowMerge(false)} merging={merging} />
      )}

      {/* Bulk Assign modal */}
      {showBulkAssign && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: C.surface, borderRadius: 8, padding: '2rem', maxWidth: '500px', width: '90%',
            border: `1px solid ${C.border}`
          }}>
            {(() => { console.log('[TEST] Bulk assign modal rendering'); return null; })()}
            <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
              Assign {selected.size} case{selected.size !== 1 ? 's' : ''} to
            </h3>
            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1.5rem', maxHeight: '400px', overflowY: 'auto' }}>
              {team?.map(member => (
                <button
                  key={member.id}
                  onClick={() => {
                    const memberId = member.id || member.user_id;
                    console.log('[BulkAssign] Clicked member button:', { memberId, member_id: member.id, user_id: member.user_id, name: member.name });
                    handleBulkAssign(memberId);
                  }}
                  disabled={bulkAssigning}
                  style={{
                    padding: '0.75rem', borderRadius: 6, cursor: bulkAssigning ? 'not-allowed' : 'pointer',
                    background: C.bg, border: `1px solid ${C.border}`, color: C.text,
                    textAlign: 'left', opacity: bulkAssigning ? 0.6 : 1,
                    transition: 'all 0.2s'
                  }}
                  onHover={e => !bulkAssigning && (e.target.style.background = C.accent + '22')}
                >
                  {member.name || member.email}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setShowBulkAssign(false); setBulkAssignee(null); }}
              disabled={bulkAssigning}
              style={{
                padding: '0.5rem 1rem', borderRadius: 6, cursor: bulkAssigning ? 'not-allowed' : 'pointer',
                background: 'transparent', border: `1px solid ${C.border}`, color: C.textMid,
                fontFamily: "'DM Mono', monospace", opacity: bulkAssigning ? 0.6 : 1
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Manual Case Creation modal */}
      {showManualCaseModal && (
        <ManualCaseCreation
          company={company}
          team={team}
          onCaseCreated={(newCase) => {
            setAlerts(prev => [newCase, ...prev]);
            setShowManualCaseModal(false);
            onOpenCase?.(newCase);
          }}
          onClose={() => setShowManualCaseModal(false)}
        />
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}